import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccounts } from '../../hooks/useAccounts';
import { accountsService } from '../../services/accountsService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';

const AccountsList: React.FC = () => {
  const { accounts, loading, error, refreshAccounts } = useAccounts();
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const navigate = useNavigate();

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.account_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (account.bank_name && account.bank_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (account.account_number && account.account_number.includes(searchTerm));

    const matchesFilter = filterType === 'all' || account.type === filterType;

    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        const { error } = await accountsService.deleteAccount(id);
        if (error) {
          alert(error.message);
        } else {
          refreshAccounts();
        }
      } catch (err) {
        alert('Failed to delete account');
      }
    }
  };

  // Show loading while authentication is in progress or business is not loaded
  if (authLoading || (isAuthenticated && !businessId)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <p className="text-gray-600">
          {authLoading ? 'Loading authentication...' : 'Loading business data...'}
        </p>
      </div>
    );
  }

  // Show message if user is authenticated but has no business
  if (isAuthenticated && !businessId) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Profile Required</h2>
          <p className="text-gray-600 mb-4">
            You need to create a business profile before you can manage accounts.
          </p>
          <p className="text-sm text-gray-500">
            Please complete your business setup first.
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">
            Please log in to access the account management system.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Accounts', current: true }
  ];

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Accounts</h1>
        <Link to="/accounts/new">
          <Button variant="primary">
            Add New Account
          </Button>
        </Link>
      </div>

      <div className="mb-6 space-y-4">
        <div className="max-w-md">
          <SearchInput
            placeholder="Search accounts by name, ID, bank name, or account number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All Types' },
            { value: 'cash', label: 'Cash' },
            { value: 'bank', label: 'Bank' },
            { value: 'other', label: 'Other' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterType(tab.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                filterType === tab.value
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          headers={['Account ID', 'Name', 'Type', 'Balance', 'Bank Details', 'Status']}
          rows={filteredAccounts.map(account => [
            <span className="font-mono text-sm">{account.account_id}</span>,
            <div>
              {account.name}
              {account.is_default && (
                <span className="ml-2 text-xs text-gray-500">(Default)</span>
              )}
            </div>,
            <span className={`badge ${accountsService.getTypeBadgeClass(account.type)}`}>
              {accountsService.getTypeLabel(account.type)}
            </span>,
            accountsService.getFormattedBalance(account.balance),
            account.type === 'bank' ? (
              <div className="text-sm">
                <div className="font-medium">{account.bank_name || 'N/A'}</div>
                <div className="text-gray-500">{account.account_number || 'N/A'}</div>
              </div>
            ) : (
              <span className="text-gray-400">-</span>
            ),
            account.is_editable ? (
              <span className="badge badge-success">Editable</span>
            ) : (
              <span className="badge badge-secondary">System</span>
            )
          ])}
          renderRow={(row, index) => {
            const account = filteredAccounts[index];
            return (
              <tr
                key={account.id}
                onClick={() => navigate(`/accounts/${account.id}`)}
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[0]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[1]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[2]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[3]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[4]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[5]}
                </td>
              </tr>
            );
          }}
          renderMobileCard={(row, index) => {
            const account = filteredAccounts[index];
            return (
              <Card
                key={account.id}
                onClick={() => navigate(`/accounts/${account.id}`)}
                className="mb-4"
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {account.name}
                        </h3>
                        <span className="text-sm font-medium text-gray-500">
                          {row[3]}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Account ID</p>
                          <p className="text-sm font-medium text-gray-900 font-mono">{row[0]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Type</p>
                          <p className="text-sm font-medium text-gray-900">{row[2]}</p>
                        </div>
                        {account.type === 'bank' && (
                          <>
                            <div>
                              <p className="text-sm text-gray-500">Bank</p>
                              <p className="text-sm font-medium text-gray-900">{account.bank_name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Account #</p>
                              <p className="text-sm font-medium text-gray-900">{account.account_number || 'N/A'}</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          }}
          emptyMessage="No accounts found"
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredAccounts.length} of {accounts.length} accounts
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountsList;